import { LogoutIcon } from "./icons/LogoutIcon";
import { SettingsIcon } from "./icons/SettingsIcon";
import { Logo } from "./icons/Logo";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { getUserLanguage, updateUserLanguage } from "@/api/language";
import { useTranslation } from "@/hooks/useTranslation";
import { Link } from "react-router";
import { useToast } from "@/components/ui/use-toast";
import { initiateJiraAuth, getJiraStatus, disconnectJira } from "@/api/jira";
import { JiraLogo } from "./icons/JiraLogo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Chevron down icon for dropdown indicators
const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

// Menu icon component (three horizontal lines)
const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

interface HeaderProps {
  isLoginPage?: boolean;
}

export const Header = ({ isLoginPage = false }: HeaderProps) => {
  const { logout, userRole, currentView, setCurrentView, setDefaultTab } =
    useAuth();
  const navigate = useNavigate();
  const [language, setLanguage] = useState("fi");
  const [isJiraConnected, setIsJiraConnected] = useState(false);
  const [isJiraConnecting, setIsJiraConnecting] = useState(false);
  const { t } = !isLoginPage ? useTranslation() : { t: (key: string) => key };
  const { toast } = useToast();

  useEffect(() => {
    async function fetchLanguage() {
      if (!isLoginPage) {
        try {
          const { language } = await getUserLanguage();
          setLanguage(language);
        } catch (error) {
          console.error("Error fetching language:", error);
        }
      }
    }
    if (!isLoginPage) {
      fetchLanguage();
    }
  }, [isLoginPage]);

  useEffect(() => {
    async function checkJiraConnection() {
      if (!isLoginPage) {
        try {
          const token = localStorage.getItem("accessToken");
          if (!token) return;
          
          const status = await getJiraStatus(token);
          setIsJiraConnected(status.connected);
        } catch (error) {
          console.error("Error checking Jira connection:", error);
        }
      }
    }
    if (!isLoginPage) {
      checkJiraConnection();
    }
  }, [isLoginPage]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      await logout(token || "");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        variant: "destructive",
        title: "Logout Error",
        description: (error as Error).message,
      });
      // Still navigate to login page even if there's an error
      navigate("/login");
    }
  };

  const handleLanguageChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    try {
      await updateUserLanguage(newLanguage);
      window.location.reload();
    } catch (error) {
      console.error("handleLanguageChange - Error updating language:", error);
    }
  };

  const handleViewChange = (
    view: "main" | "expert" | "projects" | "sales" | "timeEntries" | "projectReferences" | "approval" | "customers" | "priceLists" | "invoices" | "projectManagement" | "salary",
  ) => {
    setCurrentView(view);
    setDefaultTab("skills");
    navigate("/");
  };

  const handleJiraConnect = async () => {
    try {
      setIsJiraConnecting(true);
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast({
          variant: "destructive",
          title: t("error"),
          description: "Authentication required",
        });
        setIsJiraConnecting(false);
        return;
      }

      const { authUrl } = await initiateJiraAuth(token);

      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const authWindow = window.open(
        authUrl,
        "Jira OAuth",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Store interval ID for cleanup
      let checkClosedInterval: NodeJS.Timeout | null = null;

      // Listen for OAuth callback
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === "JIRA_AUTH_SUCCESS") {
          // Clean up interval if it exists
          if (checkClosedInterval) {
            clearInterval(checkClosedInterval);
          }
          authWindow?.close();
          window.removeEventListener("message", messageHandler);
          setIsJiraConnected(true);
          setIsJiraConnecting(false);
          toast({
            title: t("success"),
            description: t("jira.connected"),
          });
        } else if (event.data?.type === "JIRA_AUTH_ERROR") {
          // Clean up interval if it exists
          if (checkClosedInterval) {
            clearInterval(checkClosedInterval);
          }
          authWindow?.close();
          window.removeEventListener("message", messageHandler);
          toast({
            variant: "destructive",
            title: t("error"),
            description: t("jira.connectionError"),
          });
          setIsJiraConnecting(false);
        }
      };

      window.addEventListener("message", messageHandler);

      // Check if window was closed without completing auth
      checkClosedInterval = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkClosedInterval!);
          window.removeEventListener("message", messageHandler);
          setIsJiraConnecting(false);
        }
      }, 1000);
    } catch (error) {
      console.error("Error initiating Jira auth:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("jira.connectionError"),
      });
      setIsJiraConnecting(false);
    }
  };

  const handleJiraDisconnect = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      await disconnectJira(token);
      setIsJiraConnected(false);
      toast({
        title: t("success"),
        description: t("jira.disconnectSuccess"),
      });
    } catch (error) {
      console.error("Error disconnecting Jira:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: (error as Error).message,
      });
    }
  };

  return (
    <header className="fixed top-0 z-50 w-full border-b bg-background/80 backdrop-blur-[15px]">
      <div className="flex h-20 items-center gap-6 px-8">
        <div className="text-lg text-gray flex items-end align-bottom gap-4">
          <Logo
            aria-label="Skillhub logo"
            className="w-28"
            style={{ transform: "translateY(-3px)" }}
          />
        </div>
        {!isLoginPage && (
          <div className="flex justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-4 mr-6">
            {userRole?.includes("timeEntries") && (
                <p
                  className={`cursor-pointer ${
                    currentView === "timeEntries"
                      ? "text-dark"
                      : "text-gray hover:text-dark transition-colors"
                  }`}
                  onClick={() => handleViewChange("timeEntries")}
                >
                  {t("header.timeEntry")}
                </p>
              )}
              {userRole?.includes("people") && (
                <div className="relative group flex flex-col items-center">
                  <p
                    className={`cursor-pointer text-center h-[40px] flex items-center gap-1 ${
                      currentView === "approval" || currentView === "salary"
                        ? "text-dark"
                        : "text-gray hover:text-dark transition-colors"
                    }`}
                  >
                    {t("header.hr")}
                    <ChevronDownIcon />
                  </p>
                  <div
                    className="absolute hidden group-hover:block bg-white hover:border-dark-hover border-light-gray rounded-2xl mt-1"
                    style={{ top: "100%", marginTop: "-1px" }}
                  >
                    <ul className="border border-light-gray rounded-lg">
                        <li className="py-2 px-4 cursor-pointer hover:bg-light-blue text-gray whitespace-nowrap">
                          {t("header.employees")}
                        </li>
                        <li
                          className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                            currentView === "approval" ? "text-dark" : "text-gray"
                          }`}
                          onClick={() => handleViewChange("approval")}
                        >
                          {t("header.timeApproval")}
                        </li>
                        <li
                          className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                            currentView === "salary" ? "text-dark" : "text-gray"
                          }`}
                          onClick={() => handleViewChange("salary")}
                        >
                          {t("salary.formation.title")}
                        </li>
                    </ul>
                  </div>
                </div>
              )}
              {userRole?.includes("projectLead") && (
                <div className="relative group flex flex-col items-center">
                  <p
                    className={`cursor-pointer text-center h-[40px] flex items-center gap-1 ${
                      currentView === "customers" || currentView === "projectManagement" || currentView === "invoices" || currentView === "priceLists" || currentView === "projectReferences"
                        ? "text-dark"
                        : "text-gray hover:text-dark transition-colors"
                    }`}
                  >
                    {t("projectManagement.title")}
                    <ChevronDownIcon />
                  </p>
                  <div
                    className="absolute hidden group-hover:block bg-white hover:border-dark-hover border-light-gray rounded-2xl mt-1"
                    style={{ top: "100%", marginTop: "-1px" }}
                  >
                    <ul className="border border-light-gray rounded-lg">
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "customers" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("customers")}
                      >
                        {t("header.customers")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "projectManagement" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("projectManagement")}
                      >
                        {t("projectManagement.title")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "approval" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("approval")}
                      >
                        {t("header.timeApproval")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "invoices" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("invoices")}
                      >
                        {t("invoices")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "priceLists" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("priceLists")}
                      >
                        {t("customers.priceLists")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "projectReferences" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("projectReferences")}
                      >
                        {t("header.projectReferences")}
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {userRole?.includes("projects") && (
                <div className="relative group flex flex-col items-center">
                  <p
                    className={`cursor-pointer text-center h-[40px] flex items-center gap-1 ${
                      currentView === "main" || currentView === "projects" || currentView === "projectReferences" || currentView === "sales"
                        ? "text-dark"
                        : "text-gray hover:text-dark transition-colors"
                    }`}
                  >
                    {t("roles.sales")}
                    <ChevronDownIcon />
                  </p>
                  <div
                    className="absolute hidden group-hover:block bg-white hover:border-dark-hover border-light-gray rounded-2xl mt-1"
                    style={{ top: "100%", marginTop: "-1px" }}
                  >
                    <ul className="border border-light-gray rounded-lg">
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "main" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("main")}
                      >
                        {t("header.experts")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "projects" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("projects")}
                      >
                        {t("header.resourcing")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "projectReferences" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("projectReferences")}
                      >
                        {t("header.projectReferences")}
                      </li>
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "sales" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("sales")}
                      >
                        {t("header.hanselReporting")}
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {userRole?.includes("expert") && (
                <div className="relative group flex flex-col items-center">
                  <p
                    className={`cursor-pointer text-center h-[40px] flex items-center gap-1 ${
                      currentView === "expert"
                        ? "text-dark"
                        : "text-gray hover:text-dark transition-colors"
                    }`}
                  >
                    {t("myProfile")}
                    <ChevronDownIcon />
                  </p>
                  <div
                    className="absolute hidden group-hover:block bg-white hover:border-dark-hover border-light-gray rounded-2xl mt-1"
                    style={{ top: "100%", marginTop: "-1px" }}
                  >
                    <ul className="border border-light-gray rounded-lg">
                      <li
                        className={`py-2 px-4 cursor-pointer hover:bg-light-blue whitespace-nowrap ${
                          currentView === "expert" ? "text-dark" : "text-gray"
                        }`}
                        onClick={() => handleViewChange("expert")}
                      >
                        {t("header.cv")}
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {userRole?.includes("admin") && (
                <div className="relative group flex flex-col items-center">
                  <p className="cursor-pointer text-center h-[40px] flex items-center gap-1 text-gray hover:text-dark transition-colors">
                    {t("header.maintenance")}
                    <ChevronDownIcon />
                  </p>
                  <div
                    className="absolute hidden group-hover:block bg-white hover:border-dark-hover border-light-gray rounded-2xl mt-1"
                    style={{ top: "100%", marginTop: "-1px" }}
                  >
                    <ul className="border border-light-gray rounded-lg">
                      <li>
                        <Link to="/settings/general" className="block py-2 px-4 cursor-pointer hover:bg-light-blue text-gray whitespace-nowrap">
                          {t("generalSettings")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/settings/salary" className="block py-2 px-4 cursor-pointer hover:bg-light-blue text-gray whitespace-nowrap">
                          {t("salarySettings")}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            </div>
            <div className="flex items-center gap-2">
            {(userRole?.includes("timeEntries") || userRole?.includes("projectLead")) && (

              isJiraConnected ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-full hover:border-dark-hover border-light-gray h-10 w-10 cursor-pointer"
                      onClick={handleJiraDisconnect}
                      disabled={isJiraConnecting}
                      aria-label={t("jira.disconnect")}
                    >
                      <JiraLogo className="min-w-[max-content] h-[25px] text-[#2684ff]"/>
                    </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-dark text-white rounded-lg">
                      <p>{t('jira.disconnect')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-full hover:border-dark-hover border-light-gray h-10 w-10 cursor-pointer"
                      onClick={handleJiraConnect}
                      disabled={isJiraConnecting}
                      aria-label={t("jira.connect")}
                    >
                      <JiraLogo className="min-w-[max-content] h-[25px] text-light-gray"/>
                    </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-dark text-white rounded-lg">
                      <p>{t('jira.connect')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )
            )}
            {/* Menu button with dropdown */}
            <div className="relative group flex flex-col h-[50px] translate-y-[5px] items-center">
              <Button
                variant="outline"
                className="flex items-center px-2 py-2 gap-2 rounded-full hover:border-dark-hover border-light-gray w-10 h-10 cursor-pointer"
                aria-label="Menu"
              >
                <MenuIcon />
              </Button>
              <div
                className="absolute hidden group-hover:block bg-white border-light-gray rounded-2xl mt-1 min-w-[200px]"
                style={{ top: "100%", right: "10%", marginTop: "-3px" }}
              >
                <div className="border border-light-gray rounded-lg p-2">
                  {/* Language options in a row */}
                  <div className="flex gap-2 mb-2 pb-2 border-b border-light-gray">
                    <button
                      className={`flex-1 py-2 px-4 rounded-lg cursor-pointer transition-colors ${
                        language === "en"
                          ? "bg-light-blue text-dark"
                          : "text-gray hover:bg-light-blue"
                      }`}
                      onClick={() =>
                        handleLanguageChange({
                          target: { value: "en" },
                        } as React.ChangeEvent<HTMLSelectElement>)
                      }
                    >
                      EN
                    </button>
                    <button
                      className={`flex-1 py-2 px-4 rounded-lg cursor-pointer transition-colors ${
                        language === "fi"
                          ? "bg-light-blue text-dark"
                          : "text-gray hover:bg-light-blue"
                      }`}
                      onClick={() =>
                        handleLanguageChange({
                          target: { value: "fi" },
                        } as React.ChangeEvent<HTMLSelectElement>)
                      }
                    >
                      FI
                    </button>
                  </div>
                  
                  {/* Settings buttons moved to the "Ylläpito" top-level dropdown */}
                  {false && userRole?.includes("admin") && (
                    <>
                      <Link to="/settings/general" className="block">
                        <button
                          className="w-full flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-light-blue rounded-lg text-gray hover:text-dark transition-colors"
                        >
                          <SettingsIcon className="w-5 h-5" />
                          <span>{t("generalSettings")}</span>
                        </button>
                      </Link>
                      <Link to="/settings/salary" className="block">
                        <button
                          className="w-full flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-light-blue rounded-lg text-gray hover:text-dark transition-colors"
                        >
                          <SettingsIcon className="w-5 h-5" />
                          <span>{t("salarySettings")}</span>
                        </button>
                      </Link>
                    </>
                  )}
                  
                  {/* Logout button */}
                  <button
                    className="w-full flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-light-blue rounded-lg text-gray hover:text-dark transition-colors"
                    onClick={handleLogout}
                  >
                    <LogoutIcon className="w-5 h-5" />
                    <span>{t("logout")}</span>
                  </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
